import {
  Background,
  Base,
  ClarityBlue,
  ClarityGreen,
  ClarityOrange,
  ClarityPurple,
  ClarityRed,
  Content,
  Grey,
} from './colours.constants';
import { ClarityColour, ClarityShadeKey, HexPalette, HexShade } from './colours.types';

const Gray: HexPalette = {
  [HexShade[50]]: Background.Backdrop,
  [HexShade[100]]: Grey.ExtraSoft,
  [HexShade[200]]: Grey.Soft,
  [HexShade[300]]: Grey.Medium,
  [HexShade[400]]: Content.Soft,
  [HexShade[500]]: Content.Soft,
  [HexShade[600]]: Content.Medium,
  [HexShade[700]]: Content.Medium,
  [HexShade[800]]: Content.Hard,
  [HexShade[900]]: Content.Hard,
};

const Blue: HexPalette = {
  [HexShade[50]]: ClarityBlue.ExtraSoft,
  [HexShade[100]]: ClarityBlue.ExtraSoft,
  [HexShade[200]]: ClarityBlue.Soft,
  [HexShade[300]]: ClarityBlue.Soft,
  [HexShade[400]]: ClarityBlue.Medium,
  [HexShade[500]]: ClarityBlue.Hard,
  [HexShade[600]]: ClarityBlue.Hard,
  [HexShade[700]]: Base.Midnight,
  [HexShade[800]]: Base.Midnight,
  [HexShade[900]]: Base.Midnight,
};

const Purple: HexPalette = {
  [HexShade[50]]: ClarityPurple.ExtraSoft,
  [HexShade[100]]: ClarityPurple.ExtraSoft,
  [HexShade[200]]: ClarityPurple.Soft,
  [HexShade[300]]: ClarityPurple.Soft,
  [HexShade[400]]: ClarityPurple.Medium,
  [HexShade[500]]: ClarityPurple.Hard,
  [HexShade[600]]: ClarityPurple.Hard,
  [HexShade[700]]: ClarityPurple.Hard,
  [HexShade[800]]: ClarityPurple.Hard,
  [HexShade[900]]: ClarityPurple.Hard,
};

const Red: HexPalette = {
  [HexShade[50]]: ClarityRed.ExtraSoft,
  [HexShade[100]]: ClarityRed.ExtraSoft,
  [HexShade[200]]: ClarityRed.Soft,
  [HexShade[300]]: ClarityRed.Soft,
  [HexShade[400]]: ClarityRed.Medium,
  [HexShade[500]]: ClarityRed.Hard,
  [HexShade[600]]: ClarityRed.Hard,
  [HexShade[700]]: ClarityRed.Hard,
  [HexShade[800]]: ClarityRed.Hard,
  [HexShade[900]]: ClarityRed.Hard,
};

const Orange: HexPalette = {
  [HexShade[50]]: ClarityOrange.ExtraSoft,
  [HexShade[100]]: ClarityOrange.ExtraSoft,
  [HexShade[200]]: ClarityOrange.Soft,
  [HexShade[300]]: ClarityOrange.Soft,
  [HexShade[400]]: ClarityOrange.Medium,
  [HexShade[500]]: ClarityOrange.Hard,
  [HexShade[600]]: ClarityOrange.Hard,
  [HexShade[700]]: ClarityOrange.Hard,
  [HexShade[800]]: ClarityOrange.Hard,
  [HexShade[900]]: ClarityOrange.Hard,
};

const Green: HexPalette = {
  [HexShade[50]]: ClarityGreen.ExtraSoft,
  [HexShade[100]]: ClarityGreen.ExtraSoft,
  [HexShade[200]]: ClarityGreen.Soft,
  [HexShade[300]]: ClarityGreen.Soft,
  [HexShade[400]]: ClarityGreen.Medium,
  [HexShade[500]]: ClarityGreen.Hard,
  [HexShade[600]]: ClarityGreen.Hard,
  [HexShade[700]]: ClarityGreen.Hard,
  [HexShade[800]]: ClarityGreen.Hard,
  [HexShade[900]]: ClarityGreen.Hard,
};

export const HexColour = { Gray, Blue, Purple, Red, Orange, Green } as const;
export type HexColour = typeof HexColour;
export type HexColourPalette = keyof typeof HexColour;

export const RgbaColour = {
  Light: {
    5: Background.Surface,
    10: Background.Surface,
    20: Background.Surface,
    30: Background.Surface,
    40: Background.Surface,
    50: Background.Surface,
    60: Background.Surface,
    70: Background.Surface,
    80: Background.Surface,
    90: Background.Surface,
    100: Background.Surface,
  },
  Dark: {
    5: Grey.ExtraSoft,
    10: Grey.Soft,
    20: Grey.Soft,
    30: Content.Soft,
    40: Content.Soft,
    50: Content.Soft,
    60: Content.Soft,
    70: Content.Medium,
    80: Content.Medium,
    90: Content.Hard,
    100: Content.Hard,
  },
} as const;
export type RgbaColourPalette = keyof typeof RgbaColour;
export type RgbaShade = keyof (typeof RgbaColour)[RgbaColourPalette];

export const GradientColour = {
  Gradient: {
    Linear45: `linear-gradient(45deg, #3991FF 0%, #A48AFB 50.15%, #FA7066 100%)`,
    Linear90: `linear-gradient(90deg, #3991FF 0%, #A48AFB 50.15%, #FA7066 100%)`,
    Linear180: `linear-gradient(180deg, #3991FF 0%, #A48AFB 50.15%, #FA7066 100%)`,
    Linear135: `linear-gradient(135deg, #3991FF 0%, #A48AFB 50.15%, #FA7066 100%)`,
  },
} as const;
export type GradientColourPalette = keyof typeof GradientColour;
export type GradientShade = keyof (typeof GradientColour)[GradientColourPalette];

export const Colour = {
  Clarity: ClarityColour,
  ...HexColour,
  ...RgbaColour,
  ...GradientColour,
};

export type ColourPalette = keyof typeof Colour;
export type Shade = RgbaShade | GradientShade | ClarityShadeKey;
